import React, { useState, useEffect } from 'react';
import { 
  Drawer, 
  Form, 
  Input, 
  Select, 
  Button, 
  message
} from 'antd';
import { 
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  CloseOutlined,
  SaveOutlined,
  PlusOutlined
} from '@ant-design/icons';
import { giftService, GiftGiver } from '../../services/giftService';
import styles from '../common/DrawerModalMobile.module.css';

const { TextArea } = Input;
const { Option } = Select;

interface AddGiftsGiversModalMobileProps {
  isOpen: boolean;
  onClose: () => void;
  giver?: GiftGiver | null;
  mode: 'create' | 'edit';
  onSuccess: () => void;
}

export const AddGiftsGiversModalMobile: React.FC<AddGiftsGiversModalMobileProps> = ({
  isOpen,
  onClose,
  giver,
  mode,
  onSuccess
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (mode === 'edit' && giver) {
        // Pre-fill form for edit mode
        form.setFieldsValue({
          full_name: giver.full_name,
          relationship: giver.relationship,
          phone: giver.phone,
          email: giver.email,
          address: giver.address,
          social_media: giver.social_media,
          notes: giver.notes
        });
      } else if (mode === 'create') {
        // Reset form for create mode
        form.resetFields();
        form.setFieldsValue({
          relationship: 'other'
        });
      }
    }
  }, [isOpen, giver, mode]);

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);
      
      if (mode === 'create') {
        await giftService.createGiftGiver(values);
        message.success('Tạo khách mời thành công');
      } else if (mode === 'edit' && giver) {
        await giftService.updateGiftGiver(giver.id, values);
        message.success('Cập nhật khách mời thành công');
      }
      
      onSuccess();
      onClose();
    } catch (error) {
      message.error('Có lỗi xảy ra');
    } finally {
      setLoading(false);
    }
  };

  const getModalTitle = () => {
    return mode === 'create' ? 'Thêm khách mời mới' : 'Chỉnh sửa khách mời';
  };

  return (
    <Drawer
      title={getModalTitle()}
      open={isOpen}
      onClose={onClose}
      placement="bottom"
      height="90%"
      className={styles.drawer}
      bodyStyle={{ padding: 0 }}
      headerStyle={{ 
        borderBottom: '1px solid #f0f0f0',
        padding: '16px 20px'
      }}
    >
      <div className={styles.drawerContent}>
        <div className={styles.drawerBody}>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{
              relationship: 'other'
            }}
          >
            <div className={styles.formItem}>
              <Form.Item
                name="full_name"
                label="Họ và tên"
                rules={[{ required: true, message: 'Vui lòng nhập họ và tên' }]}
              >
                <Input 
                  placeholder="Nhập họ và tên đầy đủ" 
                  className={styles.inputNumber}
                  prefix={<UserOutlined />}
                />
              </Form.Item>
            </div>

            <div className={styles.formItem}>
              <Form.Item
                name="relationship"
                label="Mối quan hệ"
                rules={[{ required: true, message: 'Vui lòng chọn mối quan hệ' }]}
              >
                <Select 
                  placeholder="Chọn mối quan hệ"
                  className={styles.select}
                >
                  <Option value="family_internal">Gia đình nội</Option>
                  <Option value="family_external">Gia đình ngoại</Option>
                  <Option value="friend_bride">Bạn cô dâu</Option>
                  <Option value="friend_groom">Bạn chú rể</Option>
                  <Option value="colleague">Đồng nghiệp</Option>
                  <Option value="business_partner">Đối tác</Option>
                  <Option value="other">Khác</Option>
                </Select>
              </Form.Item>
            </div>

            <div className={styles.formItem}>
              <Form.Item
                name="phone"
                label="Số điện thoại"
              >
                <Input 
                  placeholder="Nhập số điện thoại" 
                  className={styles.inputNumber}
                  prefix={<PhoneOutlined />}
                />
              </Form.Item>
            </div>

            <div className={styles.formItem}>
              <Form.Item
                name="email"
                label="Email"
              >
                <Input 
                  placeholder="Nhập địa chỉ email" 
                  className={styles.inputNumber}
                  prefix={<MailOutlined />}
                />
              </Form.Item>
            </div>

            <div className={styles.formItem}>
              <Form.Item
                name="social_media"
                label="Mạng xã hội"
              >
                <Input 
                  placeholder="Facebook, Zalo, v.v." 
                  className={styles.inputNumber}
                />
              </Form.Item>
            </div>

            <div className={styles.formItem}>
              <Form.Item
                name="address"
                label="Địa chỉ"
              >
                <TextArea 
                  rows={2} 
                  placeholder="Nhập địa chỉ chi tiết"
                  className={styles.textArea}
                />
              </Form.Item>
            </div>

            <div className={styles.formItem}>
              <Form.Item
                name="notes"
                label="Ghi chú"
              >
                <TextArea 
                  rows={3} 
                  placeholder="Nhập ghi chú hoặc click vào các nút bên dưới"
                  className={styles.textArea}
                />
              </Form.Item>
            </div>

            <div className={styles.formItem}>
              <Form.Item label="Ghi chú mẫu">
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
                  {[
                    'HR Potique', 'FB Potique', 'An Ninh Potique', 'Kỹ Thuật Potique',
                    'FO Potique', 'Bếp Potique', 'KT Potique', 'Cứu hộ Potique',
                    'Bell Potique', 'Học DL', 'Bạn C2', 'Bạn C3', 'Bạn C1',
                    'Bạn thân', 'Trà Yoga', 'KS MaJestic'
                  ].map((note, index) => (
                    <Button
                      key={index}
                      size="small"
                      type="dashed"
                      style={{ fontSize: '11px', padding: '4px 8px', height: '28px', borderRadius: '4px' }}
                      onClick={() => {
                        const currentNotes = form.getFieldValue('notes') || '';
                        const newNotes = currentNotes ? `${currentNotes}, ${note}` : note;
                        form.setFieldsValue({ notes: newNotes });
                      }}
                    >
                      {note}
                    </Button>
                  ))}
                </div>
              </Form.Item>
            </div>
          </Form>
        </div>

        <div className={styles.drawerFooter}>
          <div className={styles.drawerFooterRow}>
            <Button
              className={`${styles.drawerFooterButton} ${styles.default}`}
              onClick={onClose}
              icon={<CloseOutlined />}
            >
              Hủy
            </Button>
            <Button
              type="primary"
              className={`${styles.drawerFooterButton} ${styles.primary}`}
              onClick={() => form.submit()}
              icon={mode === 'create' ? <PlusOutlined /> : <SaveOutlined />}
              loading={loading}
            >
              {mode === 'create' ? 'Thêm khách mời' : 'Cập nhật'}
            </Button>
          </div>
        </div>
      </div>
    </Drawer>
  );
};
